// src/pages/VisitsPage.jsx
import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import VisitsHeader from '../components/visits/VisitsHeader';
import VisitsStats from '../components/visits/VisitsStats';
import VisitsFilters from '../components/visits/VisitsFilters';
import VisitsTable from '../components/visits/VisitsTable';
import VisitDetailsView from '../components/visits/VisitDetailsView';

const API_BASE_URL = 'https://patrol-visit.patroll-management.nissiindia.com';

const VisitsPage = () => {
  // Filters / query
  const [statusFilter, setStatusFilter] = useState('all'); // all | captured | uploaded | delivered | ...
  const [approveFilter, setApproveFilter] = useState('all'); // all | pending | approved | rejected
  const [runTypeFilter, setRunTypeFilter] = useState('all'); // all | auto | manual
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const navigate = useNavigate();

  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Data
  const [visits, setVisits] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null); // for approve/reject buttons

  // Email sending state
  const [sendingMail, setSendingMail] = useState(false);

  const totalVisits = pagination.total || 0;

  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  // Fetch list of visits
  const fetchVisits = async (opts = {}) => {
    try {
      setLoading(true);
      setError('');
      const currentPage = opts.page || page;

      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', limit);

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (approveFilter !== 'all') params.append('approve_status', approveFilter);
      if (runTypeFilter !== 'all') params.append('run_type', runTypeFilter);
      if (search.trim()) params.append('search', search.trim());
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/visits?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to load visits');
      }

      const data = json.data || [];
      const pag = json.pagination || {
        page: currentPage,
        limit,
        total: data.length,
        totalPages: 1,
      };
      console.log('data:',data);

      setVisits(data);
      setPagination({
        page: pag.page,
        limit: pag.limit,
        total: pag.total,
        totalPages: pag.totalPages || Math.max(1, Math.ceil(pag.total / pag.limit)),
      });
    } catch (err) {
      console.error('[fetchVisits]', err);
      setVisits([]);
      setError(err.message || 'Failed to load visits');
      toast.error(err.message || 'Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  // Fetch when filters/search/date change
  useEffect(() => {
    setPage(1);
    fetchVisits({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, approveFilter, runTypeFilter, search, dateFrom, dateTo]);

  // Fetch when page changes
  useEffect(() => {
    fetchVisits({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // View full details (full page expansion)
  const handleViewVisit = async (visit) => {
    try {
      setDetailsLoading(true);
      setSelectedVisit(null);

      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/visits/${visit.visit_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to load visit details');
      }

      const fullVisit = json.data || json;
      console.log('response:', fullVisit);
      setSelectedVisit(fullVisit);
    } catch (err) {
      console.error('[viewVisit]', err);
      toast.error(err.message || 'Failed to load visit details');
      setSelectedVisit(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleBackFromDetails = () => {
    setSelectedVisit(null);
  };

  // Approve / Reject actions (from table row)
  const handleApproveReject = async (visit, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this visit?`)) {
      return;
    }

    try {
      setApprovingId(visit.visit_id);
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/visits/${visit.visit_id}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action }),
        }
      );

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || `Failed to ${action} visit`);
      }

      toast.success(
        action === 'approve' ? 'Visit approved successfully' : 'Visit rejected'
      );
      // Refresh list
      fetchVisits({ page });
    } catch (err) {
      console.error('[approveRejectVisit]', err);
      setError(err.message || `Failed to ${action} visit`);
      toast.error(err.message || `Failed to ${action} visit`);
    } finally {
      setApprovingId(null);
    }
  };

  // Send manual visit mail (used by VisitDetailsView)
  const handleSendVisitMail = async (visit) => {
    try {
      if (!visit?.visit_id) return;

      // safeguarded: backend requires approve_status === 'approved'
      if (visit.approve_status !== 'approved') {
        toast.error('Visit must be approved before sending email.');
        return;
      }

      setSendingMail(true);

      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      // Backend endpoint: POST /api/visits/:id/resend
      const res = await fetch(
        `${API_BASE_URL}/api/visits/${visit.visit_id}/resend`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to send visit email');
      }

      toast.success('Visit email sent successfully!');
    } catch (err) {
      console.error('[sendVisitMail]', err);
      toast.error(err.message || 'Failed to send visit email');
    } finally {
      setSendingMail(false);
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'text-sm',
        }}
      />

      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        {/* If a visit is selected → show full details page */}
        {selectedVisit ? (
          <VisitDetailsView
            visit={selectedVisit}
            loading={detailsLoading}
            onBack={handleBackFromDetails}
            onSendMail={handleSendVisitMail}
            sendingMail={sendingMail}
          />
        ) : (
          <>
            <VisitsHeader
              search={search}
              onSearchChange={setSearch}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
            />

            <VisitsStats totalVisits={totalVisits} />

            <VisitsFilters
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              approveFilter={approveFilter}
              onApproveFilterChange={setApproveFilter}
              runTypeFilter={runTypeFilter}
              onRunTypeFilterChange={setRunTypeFilter}
              visibleCount={visits.length}
              totalVisits={totalVisits}
            />

            <VisitsTable
              visits={visits}
              loading={loading}
              error={error}
              pagination={pagination}
              onPageChange={setPage}
              onView={handleViewVisit}
              onApprove={(visit) => handleApproveReject(visit, 'approve')}
              onReject={(visit) => handleApproveReject(visit, 'reject')}
              approvingId={approvingId}
            />
          </>
        )}
      </main>
    </>
  );
};

export default VisitsPage;
